import * as React from "react";
import type { PlatformAdmin, PlatformAdminLoginResponse } from "@repo/types";
import { PlatformAdminApi } from "@repo/api-client";
import { platformAdminSessionStorage } from "@repo/lib";

interface PlatformAdminContextValue {
  admin: PlatformAdmin | null;
  token: string | null;
  loading: boolean;
  login: (payload: { username: string; password: string; remember?: boolean }) => Promise<PlatformAdminLoginResponse>;
  logout: () => Promise<void>;
}

const PlatformAdminContext = React.createContext<PlatformAdminContextValue | undefined>(undefined);

export const PlatformAdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = React.useState<PlatformAdmin | null>(null);
  const [token, setToken] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const session = platformAdminSessionStorage.loadSession();
    if (session) {
      setAdmin(session.admin);
      setToken(session.token);
    }
    setLoading(false);
  }, []);

  const login = React.useCallback<PlatformAdminContextValue["login"]>(async (payload) => {
    setLoading(true);
    try {
      const data = await PlatformAdminApi.login(payload);
      if (data.success) {
        setAdmin(data.admin);
        setToken(data.token);
      }
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = React.useCallback(async () => {
    await PlatformAdminApi.logout();
    setAdmin(null);
    setToken(null);
  }, []);

  const value = React.useMemo<PlatformAdminContextValue>(
    () => ({
      admin,
      token,
      loading,
      login,
      logout
    }),
    [admin, token, loading, login, logout]
  );

  return <PlatformAdminContext.Provider value={value}>{children}</PlatformAdminContext.Provider>;
};

export const usePlatformAdmin = () => {
  const context = React.useContext(PlatformAdminContext);
  if (!context) {
    throw new Error("usePlatformAdmin must be used within PlatformAdminProvider");
  }
  return context;
};
